// @ts-check
// Linha entre os passos com o "+" no meio e o rótulo dos caminhos (spec 2.0.0, fluxos.md seção 20.5; contrato 09 de
// fluxos, seção 17). O "+" marca o ponto de inserção (o catálogo abre em "Inserir aqui") e, enquanto uma ação do
// catálogo é arrastada, vira uma área de soltar. Depois do último passo de cada caminho há um "+" e nada mais.
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { Icon } from '../../ui/icon.js';
import { FlowCountLink } from './flow-ui.js';
import { t } from '../../i18n/index.js';

export const FLOW_ACTION_DRAG_TYPE = 'application/x-appcrm-flow-action';

/**
 * @typedef {import('../../lib/flow-editor-state.js').FlowInsertPoint} FlowInsertPoint
 */

/**
 * @param {{ point: FlowInsertPoint, label: string, isActive?: boolean, isDragging?: boolean, isPulsing?: boolean, disabledReason?: string | null,
 *   isReadOnly?: boolean, isEnd?: boolean, onInsert?: (point: FlowInsertPoint, element: HTMLElement) => void,
 *   onDropAction?: (point: FlowInsertPoint, itemKey: string) => void }} props
 */
export function FlowConnector({ point, label, isActive = false, isDragging = false, isPulsing = false, disabledReason = null, isReadOnly = false, isEnd = false, onInsert, onDropAction }) {
  const [isOver, setIsOver] = useState(false);
  if (isReadOnly) return html`<div class="flow-connector" data-end=${isEnd ? 'true' : undefined} aria-hidden="true"><span class="flow-connector-dot"></span></div>`;
  const isDisabled = Boolean(disabledReason);
  return html`<div class="flow-connector" data-end=${isEnd ? 'true' : undefined} data-dragging=${isDragging && !isDisabled ? 'true' : undefined}
    data-over=${isOver ? 'true' : undefined}
    onDragOver=${(/** @type {DragEvent} */ event) => {
      if (!isDragging || isDisabled) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
      if (!isOver) setIsOver(true);
    }}
    onDragLeave=${() => setIsOver(false)}
    onDrop=${(/** @type {DragEvent} */ event) => {
      setIsOver(false);
      if (isDisabled) return;
      const itemKey = event.dataTransfer?.getData(FLOW_ACTION_DRAG_TYPE) || event.dataTransfer?.getData('text/plain');
      if (!itemKey) return;
      event.preventDefault();
      onDropAction?.(point, itemKey);
    }}>
    <button type="button" class="flow-plus-button" data-active=${isActive ? 'true' : undefined} data-pulse=${isPulsing && !isDisabled ? 'true' : undefined}
      aria-label=${label} title=${disabledReason ?? label} disabled=${isDisabled} tabindex="-1"
      onClick=${(/** @type {MouseEvent} */ event) => {
        event.stopPropagation();
        onInsert?.(point, /** @type {HTMLElement} */ (event.currentTarget));
      }}>
      <${Icon} name="IconPlus" size=${14} />
    </button>
  </div>`;
}

const BRANCH_LABEL_KEYS = /** @type {Record<string, string>} */ ({ yes: 'flows.branches.yes', no: 'flows.branches.no', beforeDate: 'flows.branches.beforeDate', afterDate: 'flows.branches.afterDate' });

/** Texto do caminho (SIM, NÃO, ATÉ O DIA, DEPOIS DO DIA). @param {string} branchKey */
export const branchLabelText = (branchKey) => t(BRANCH_LABEL_KEYS[branchKey] ?? 'flows.branches.yes');

/**
 * Rótulo do caminho, com a quantidade que seguiu por ele quando os números estão ligados.
 * @param {{ branchKey: string, count?: number | null, flowId?: string | null, splitId?: string, position?: number }} props
 */
export function FlowBranchLabel({ branchKey, count = null, flowId = null, splitId, position = 0 }) {
  return html`<span class="flow-branch-label-wrap">
    <span class="flow-branch-label" data-branch=${branchKey}>${branchLabelText(branchKey)}</span>
    ${count !== null && flowId && splitId
      ? html`<${FlowCountLink} flowId=${flowId} nodeKey=${splitId} metric=${position === 0 ? 'BRANCH_FIRST' : 'BRANCH_SECOND'} value=${count}
          className="flow-branch-count" label=${t('flows.counts.branch', { count, branch: branchLabelText(branchKey) })} tabIndex=${-1} />`
      : null}
  </span>`;
}
